import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { DashboardNavbar } from '../../components/DashboardNavbar'
import { FloatingActionBar } from '../../components/FloatingActionBar'
import { useNavigation } from '../../contexts/NavigationContext'
import {
  BreadcrumbsCarousel,
  cn,
  ScrollFade,
  SubBar,
  type BadgeProps,
  type BreadcrumbItem,
  type ButtonProps,
} from '@valiant/ui-shared'

interface FloatingActionBarControlledProps {
  notificationsOpen?: boolean
  onNotificationsChange?: (open: boolean) => void
  depositFundsOpen?: boolean
  onDepositFundsChange?: (open: boolean) => void
  swapOpen?: boolean
  onSwapChange?: (open: boolean) => void
}

interface MainLayoutProps extends FloatingActionBarControlledProps {
  children: ReactNode
  className?: string
  contentClassName?: string
  heightClassName?: string
  modalsComponent?: ReactNode
  subBar?: ReactNode
  showFloatingActionBar?: boolean
  redirectOnSelect?: boolean
  breadcrumbs?: BreadcrumbItem[]
  hideBack?: boolean
  title?: string
  headerTitle?: string
  headerImage?: string | React.ReactNode
  badges?: BadgeProps[]
  buttons?: ButtonProps[]
  subBarOverride?: ReactNode
}

export function MainLayout({
  children,
  className,
  contentClassName,
  heightClassName,
  modalsComponent,
  showFloatingActionBar = true,
  redirectOnSelect = true,
  breadcrumbs = [],
  hideBack = false,
  title,
  headerTitle,
  headerImage,
  badges,
  buttons,
  subBarOverride,
}: MainLayoutProps) {
  const {
    notificationsOpen,
    depositFundsOpen,
    setNotificationsOpen,
    setDepositFundsOpen,
    swapOpen,
    setSwapOpen,
    profileOpen,
    setProfileOpen,
    addWhitelistedAddressOpen,
    setAddWhitelistedAddressOpen,
  } = useNavigation()

  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 0)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleNotificationsChange = (open: boolean) => {
    setNotificationsOpen(open)
  }

  const handleDepositFundsChange = (open: boolean) => {
    setDepositFundsOpen(open)
  }

  const handleSwapChange = (open: boolean) => {
    setSwapOpen(open)
  }

  const handleProfileChange = (open: boolean) => {
    setProfileOpen(open)
  }

  const handleAddWhitelistedAddressChange = (open: boolean) => {
    setAddWhitelistedAddressOpen(open)
  }

  const finalBreadcrumbs = useMemo(() => {
    const lastItem = breadcrumbs[breadcrumbs.length - 1]
    const enhancedBreadcrumbs =
      breadcrumbs.length < 1
        ? breadcrumbs
        : [
            ...breadcrumbs.slice(0, breadcrumbs.length - 1),
            {
              ...lastItem,
              badges: badges || lastItem.badges,
              buttons: buttons || lastItem.buttons,
            },
          ]
    return [...enhancedBreadcrumbs]
  }, [breadcrumbs, badges, buttons])

  const subBar = useMemo(() => {
    if (subBarOverride) {
      return subBarOverride
    }

    if (!title && !headerTitle) return null

    return (
      <SubBar
        hideBack={hideBack}
        title={title}
        headerTitle={headerTitle}
        headerImage={headerImage}
        badges={badges}
        buttons={buttons}
      />
    )
  }, [title, headerTitle, headerImage, badges, buttons, hideBack, subBarOverride])

  return (
    <div className={cn(`min-h-screen w-full pb-[64px] md:pb-0`, className)}>
      <div className={cn('flex flex-col w-full', heightClassName)}>
        {/* Mobile: sticky header with navbar + breadcrumbs */}
        <ScrollFade isScrolled={isScrolled} className="md:hidden">
          <DashboardNavbar redirectOnSelect={redirectOnSelect} hideBack={hideBack} />

          <div className="block">
            <BreadcrumbsCarousel breadcrumbs={finalBreadcrumbs || []} />
          </div>

          {subBarOverride ? (
            <div className="mx-4 md:block md:mx-14 md:mt-4">{subBarOverride}</div>
          ) : null}
        </ScrollFade>

        {/* Desktop: navbar sticky, subbar normal flow */}
        <ScrollFade isScrolled={isScrolled} className="hidden md:block">
          <DashboardNavbar redirectOnSelect={redirectOnSelect} hideBack={hideBack} />
        </ScrollFade>

        {subBar ? <div className="mx-4 hidden md:block md:mx-14 md:mt-4">{subBar}</div> : null}
        <main
          className={cn(
            `flex-1 space-y-8 mx-4 mt-4 md:mx-14 md:pr-[70px] mb-2`,
            contentClassName ?? '',
          ).trim()}
        >
          {children}
        </main>
      </div>
      {showFloatingActionBar ? (
        <FloatingActionBar
          notificationsOpen={notificationsOpen}
          onNotificationsChange={handleNotificationsChange}
          depositFundsOpen={depositFundsOpen}
          onDepositFundsChange={handleDepositFundsChange}
          swapOpen={swapOpen}
          onSwapChange={handleSwapChange}
          addWhitelistedAddressOpen={addWhitelistedAddressOpen}
          onAddWhitelistedAddressChange={handleAddWhitelistedAddressChange}
          profileOpen={profileOpen}
          onProfileChange={handleProfileChange}
        />
      ) : null}
      {modalsComponent ? modalsComponent : null}
    </div>
  )
}
